/**
 * LeftShield Admin API Service
 * Platform administration API client for managing customer tenants
 */

import type {
  TenantSummary,
  TenantDetail,
  CreateTenantRequest,
  UpdateTenantRequest,
  TrialTenantsList,
  CreateTrialTenantRequest,
  TrialTenantDto,
  PlatformMetrics,
  TenantActivityMetrics,
  AuditLogEntry,
  AuditLogFilter,
  PaginatedResponse,
  PlatformUser,
  InvitePlatformUserRequest,
  UpdatePlatformUserRequest,
  PlatformSettings,
  UpdatePlatformSettingsRequest,
  DetailedPlatformMetrics,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://localhost:7001/api';

// =============================================================================
// TOKEN PROVIDER
// =============================================================================

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider | null = null;

/**
 * Set the token provider function used for API authentication.
 * This should be called once from a component that has access to the auth context.
 */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

// =============================================================================
// HTTP CLIENT
// =============================================================================

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Get authentication token if provider is set
  if (tokenProvider) {
    const token = await tokenProvider();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

// =============================================================================
// TENANT MANAGEMENT API
// =============================================================================

export const tenantsApi = {
  /**
   * Get all tenants with optional filtering
   */
  async getAll(includeInactive = false): Promise<TenantSummary[]> {
    const params = new URLSearchParams();
    if (includeInactive) params.set('includeInactive', 'true');
    return fetchWithAuth(`/platform/tenants?${params}`);
  },

  /**
   * Get tenant details by ID
   */
  async getById(tenantId: string): Promise<TenantDetail> {
    return fetchWithAuth(`/platform/tenants/${tenantId}`);
  },

  /**
   * Create a new tenant
   */
  async create(request: CreateTenantRequest): Promise<TenantDetail> {
    return fetchWithAuth('/platform/tenants', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * Update tenant details
   */
  async update(tenantId: string, request: UpdateTenantRequest): Promise<TenantDetail> {
    return fetchWithAuth(`/platform/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  /**
   * Suspend a tenant
   */
  async suspend(tenantId: string, reason: string): Promise<void> {
    return fetchWithAuth(`/platform/tenants/${tenantId}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Reactivate a suspended tenant
   */
  async reactivate(tenantId: string): Promise<void> {
    return fetchWithAuth(`/platform/tenants/${tenantId}/reactivate`, {
      method: 'POST',
    });
  },

  /**
   * Get activity metrics for a tenant
   */
  async getActivityMetrics(tenantId: string): Promise<TenantActivityMetrics> {
    return fetchWithAuth(`/platform/tenants/${tenantId}/metrics`);
  },
};

// =============================================================================
// TRIAL MANAGEMENT API
// =============================================================================

export const trialsApi = {
  /**
   * Get all trial tenants
   */
  async getAll(includeExpired = false): Promise<TrialTenantsList> {
    const params = new URLSearchParams();
    if (includeExpired) params.set('includeExpired', 'true');
    return fetchWithAuth(`/platform/trials?${params}`);
  },

  /**
   * Create a new trial tenant
   */
  async create(request: CreateTrialTenantRequest): Promise<TrialTenantDto> {
    return fetchWithAuth('/platform/trials', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * Extend trial duration
   */
  async extend(tenantId: string, additionalDays: number): Promise<void> {
    return fetchWithAuth(`/platform/trials/${tenantId}/extend`, {
      method: 'POST',
      body: JSON.stringify({ additionalDays }),
    });
  },

  /**
   * Convert trial to paid subscription
   */
  async convert(tenantId: string, subscriptionTier: string): Promise<void> {
    return fetchWithAuth(`/platform/trials/${tenantId}/convert`, {
      method: 'POST',
      body: JSON.stringify({ subscriptionTier }),
    });
  },
};

// =============================================================================
// METRICS API
// =============================================================================

export const metricsApi = {
  /**
   * Get platform-wide metrics
   */
  async getPlatformMetrics(): Promise<PlatformMetrics> {
    return fetchWithAuth('/platform/metrics');
  },

  /**
   * Get activity metrics for all tenants
   */
  async getTenantActivityMetrics(): Promise<TenantActivityMetrics[]> {
    return fetchWithAuth('/platform/metrics/tenants');
  },
};

// =============================================================================
// AUDIT LOG API
// =============================================================================

export const auditApi = {
  /**
   * Get audit log entries with pagination and filtering
   */
  async getEntries(
    filter: AuditLogFilter = {},
    page = 1,
    pageSize = 50
  ): Promise<PaginatedResponse<AuditLogEntry>> {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    if (filter.action) params.set('action', filter.action);
    if (filter.tenantId) params.set('tenantId', filter.tenantId);
    if (filter.actorId) params.set('actorId', filter.actorId);
    return fetchWithAuth(`/platform/audit?${params}`);
  },
};

// =============================================================================
// PLATFORM USER MANAGEMENT API
// =============================================================================

export const identityApi = {
  /**
   * Get all platform users
   */
  async getAll(): Promise<PlatformUser[]> {
    return fetchWithAuth('/platform/users');
  },

  /**
   * Get platform user by ID
   */
  async getById(userId: string): Promise<PlatformUser> {
    return fetchWithAuth(`/platform/users/${userId}`);
  },

  /**
   * Invite a new platform user
   */
  async invite(request: InvitePlatformUserRequest): Promise<PlatformUser> {
    return fetchWithAuth('/platform/users/invite', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * Update platform user
   */
  async update(userId: string, request: UpdatePlatformUserRequest): Promise<PlatformUser> {
    return fetchWithAuth(`/platform/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  /**
   * Disable a platform user
   */
  async disable(userId: string): Promise<void> {
    return fetchWithAuth(`/platform/users/${userId}/disable`, {
      method: 'POST',
    });
  },

  /**
   * Enable a platform user
   */
  async enable(userId: string): Promise<void> {
    return fetchWithAuth(`/platform/users/${userId}/enable`, {
      method: 'POST',
    });
  },

  /**
   * Resend invitation email
   */
  async resendInvitation(userId: string): Promise<void> {
    return fetchWithAuth(`/platform/users/${userId}/resend-invitation`, {
      method: 'POST',
    });
  },

  /**
   * Reset MFA for a user
   */
  async resetMfa(userId: string): Promise<void> {
    return fetchWithAuth(`/platform/users/${userId}/reset-mfa`, {
      method: 'POST',
    });
  },
};

// =============================================================================
// PLATFORM SETTINGS API
// =============================================================================

export const settingsApi = {
  /**
   * Get platform settings
   */
  async get(): Promise<PlatformSettings> {
    return fetchWithAuth('/platform/settings');
  },

  /**
   * Update platform settings
   */
  async update(request: UpdatePlatformSettingsRequest): Promise<PlatformSettings> {
    return fetchWithAuth('/platform/settings', {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  /**
   * Test Slack webhook connection
   */
  async testSlackWebhook(webhookUrl: string): Promise<{ success: boolean; message: string }> {
    return fetchWithAuth('/platform/settings/test-slack', {
      method: 'POST',
      body: JSON.stringify({ webhookUrl }),
    });
  },
};

// =============================================================================
// DETAILED METRICS API
// =============================================================================

export const detailedMetricsApi = {
  /**
   * Get detailed platform metrics with history
   */
  async getDetailedMetrics(days = 30): Promise<DetailedPlatformMetrics> {
    return fetchWithAuth(`/platform/metrics/detailed?days=${days}`);
  },

  /**
   * Get API usage breakdown
   */
  async getApiUsage(startDate?: string, endDate?: string): Promise<{ endpoints: Array<{ endpoint: string; method: string; count: number; avgMs: number }> }> {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return fetchWithAuth(`/platform/metrics/api-usage?${params}`);
  },

  /**
   * Get tenant activity rankings
   */
  async getTenantRankings(limit = 10): Promise<TenantActivityMetrics[]> {
    return fetchWithAuth(`/platform/metrics/tenant-rankings?limit=${limit}`);
  },
};
